<template>
  <ScrollContainer
    ref="scrollcontainer"
    :class="$style['circle-list__container']">
    <template v-if="!!$slots.header" #header>
      <slot name="header" />
    </template>
    <div
      ref="root"
      :class="[
        $style['circle-list'],
        { [$style['circle-list--small']]: small }
      ]">
      <NuxtLink
        v-for="item in items"
        :key="item.id"
        :ref="item.slug === active ? 'activeEl' : null"
        :to="item.slug"
        active-class=""
        exact-active-class=""
        data-test-id="circle-list-item"
        :class="[
          $style['circle-list__item'],
          { [$style['circle-list__item--disabled']]: disabled },
          { [$style['circle-list__item--active']]: item.slug === active }
        ]">
        <div :class="$style['circle-list__image']">
          <img
            :key="item.image"
            ref="images"
            :src="item.image"
            fetchpriority="low"
            loading="lazy"
            alt="thumbnail image">
        </div>
        <div :class="$style['circle-list__label']">
          {{ item.title }}
        </div>
      </NuxtLink>
    </div>
  </ScrollContainer>
</template>
<script setup>
const root = ref(null)
const scrollcontainer = ref(null)
const images = ref()

const $style = useCssModule()
const props = defineProps({
  items: {
    type: Array,
    default: null
  },
  small: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  active: {
    type: String,
    default: undefined
  }
})

const scrollIntoView = async () => {
  await requestAnimationFrame(async () => {
    await nextTick()
    const activeEl = root.value?.querySelector(`.${$style['circle-list__item--active']}`)
    if (!activeEl) { return }
    scrollcontainer.value.$wrapper.scroll({ left: activeEl.offsetLeft - 100 || 0, behavior: 'instant' })
  })

  setTimeout(() => {
    correctSafariLazy()
  }, 100) // wait for scroll event to finish and any elements with width to be rendered
}

const correctSafariLazy = () => {
  if (!images.value) { return }
  let imageWidth = 0

  // make sure we have an image-width of non-zero (that's actually rendered, either the first or the first one in view)
  for (let i = 0; i < images.value.length; i++) {
    const image = images.value[i]
    const { width } = image.getBoundingClientRect()
    if (width !== 0) {
      imageWidth = width
      break // directly stop looking for width once we find a valid one
    }
  }

  images.value.forEach(image => {
    const { x } = image.getBoundingClientRect()
    if (x <= 0 && x + imageWidth > 0) {
      image.loading = 'eager'
    }
  })
}

onMounted(async () => await scrollIntoView())
onActivated(async () => await scrollIntoView())
watch(() => props.active, async () => await scrollIntoView(), { deep: true })
</script>
<style lang="scss" module>
$item-width: clamp(110px , 32cqw, 180px);
$item-width-tablet: clamp(110px , 23.5cqw, 180px);
$item-width-desktop: calc(var(--spacing) * 15);

.circle-list {
  display: flex;
  gap: calc(var(--spacing) * 1.5);
  margin-right: $item-width;
  padding: var(--spacing);

  @media (min-width: #{$breakpoint-mobile}) {
    margin-right: $item-width-tablet;
  }

  @media (min-width: #{$breakpoint-portrait-tablet}) {
    margin-right: $item-width-desktop;
  }

  &__image {
    border-radius: 100%;
    display: flex;
    object-fit: cover;
    align-items: center;
    width: $item-width;
    height: $item-width;
    overflow: hidden;
    position: relative;
    transition: opacity var(--transition-time) var(--transition-easing);

    @media (min-width: #{$breakpoint-mobile}) {
      width: $item-width-tablet;
      height: $item-width-tablet;
    }

    @media (min-width: #{$breakpoint-portrait-tablet}) {
      width: $item-width-desktop;
      height: $item-width-desktop;
    }

    &::after {
      box-shadow: inset 0 0 0 2px var(--grey-light), inset 0 0 0 4px var(--white);
      border-radius: 100%;
      display: block;
      position: absolute;
      inset: 0;
      content: '';
      z-index: 2;
      transition: box-shadow var(--transition-time) var(--transition-easing);
    }
  }

  &__label {
    font-size: var(--font-size-s);
    line-height: 1.3;
    margin-top: var(--spacing);
    width: 100%;
  }

  &--small {
    gap: var(--spacing);

    .circle-list__image {
      width: calc(var(--spacing) * 10);
      height: calc(var(--spacing) * 10);
    }

    .circle-list__label {
      width: calc(var(--spacing) * 1)4;
      font-size: var(--font-size-xs);
    }
  }

  &__item {
    color: var(--grey-dark);
    display: flex;
    flex-direction: column;
    align-items: center;
    text-decoration: none;
    text-align: center;
    width: $item-width;

    @media (min-width: #{$breakpoint-mobile}) {
      width: $item-width-tablet;
    }

    @media (min-width: #{$breakpoint-portrait-tablet}) {
      width: $item-width-desktop;
    }

    &:hover:not(.circle-list__item--active),
    &:active {
      .circle-list__image::after {
        box-shadow: inset 0 0 0 2px var(--grey), inset 0 0 0 4px var(--white);
      }
    }

    &--active {
      color: var(--black);

      .circle-list__image::after {
        box-shadow: inset 0 0 0 2px var(--black), inset 0 0 0 4px var(--white);
      }
    }

    &--disabled{
      pointer-events: none;
    }
  }

}
</style>
